'use client';

import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';

import { AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { useAuthStore } from '@/stores/AuthStore';

import { type Policy, PopularPolicyClient } from './PopularPolicyClient';

const interleave = <T,>(arr1: T[], arr2: T[]): T[] => {
  const result: T[] = [];
  const length = Math.max(arr1.length, arr2.length);

  for (let i = 0; i < length; i++) {
    if (i < arr1.length) result.push(arr1[i]);
    if (i < arr2.length) result.push(arr2[i]);
  }

  return result;
};

const mapAuthPolicy = (
  item: {
    metaPolicyIdIdx?: string;
    policyName?: string;
    supportBenefit?: string;
    readCount?: number;
    dday?: string;
  },
  badge: 'peer' | 'neighbor',
): Policy | null => {
  if (!item.metaPolicyIdIdx) return null;

  return { ...item, badge } as Policy;
};

const AuthPopularPolicySection = () => {
  const [peerRes, neighborRes] = useSuspenseQueries({
    queries: [
      AuthQueryOptions['get:/wello/v2/auth/member/peer-group/policy'](),
      AuthQueryOptions['get:/wello/v2/auth/member/neighbor/policy'](),
    ],
  });

  const peerPolicies =
    peerRes.data?.context?.policyList
      ?.map((item) => mapAuthPolicy(item, 'peer'))
      .filter((p): p is Policy => p !== null)
      .slice(0, 3) ?? [];

  const peerIds = new Set(peerPolicies.map((p) => p.metaPolicyIdIdx));

  const neighborPolicies =
    neighborRes.data?.context?.policyList
      ?.map((item) => mapAuthPolicy(item, 'neighbor'))
      .filter(
        (p): p is Policy => p !== null && !peerIds.has(p.metaPolicyIdIdx),
      )
      .slice(0, 3) ?? [];

  const policies = interleave(peerPolicies, neighborPolicies);

  if (policies.length === 0) return null;

  return <PopularPolicyClient isLogin policies={policies} />;
};

const UnauthPopularPolicySection = () => {
  const { data } = useSuspenseQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/policy/popular'](),
  });

  const policies =
    data?.context?.policyList
      ?.filter((item) => item?.id)
      .map(
        (item) =>
          (({
            metaPolicyIdIdx: item.metaPolicyIdIdx,
            policyName: item.policyName,
            supportBenefit: item.supportBenefit,
            readCount: item.readCount,
            dday: item.dday
          }) as Policy),
      ) ?? [];

  if (policies.length === 0) return null;

  return (
    <PopularPolicyClient isLogin={false} policies={policies.slice(0, 10)} />
  );
};

export const PopularPolicySection = () => {
  const isLogin = useAuthStore((state) => state.isLogin);

  return isLogin ? (
    <AuthPopularPolicySection />
  ) : (
    <UnauthPopularPolicySection />
  );
};
